import { UpdatePage } from '@components/molecules';
import { TextFieldElement, SwitchElement, useForm } from 'react-hook-form-mui';
import API from '@apps/accounts/services/user';
import { ModelProps } from '@/types';
import { UserSchema, UserSchemaType } from '@apps/accounts/schemas/user';
import { JSX } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import CountryAPI from '@apps/localization/services/country';
import { useAuth } from '@/contexts/auth';
import UnitAPI from '@apps/localization/services/unit';
import FunctionalAreaAPI from '@apps/nnc/services/functionalarea';
import GroupsAPI from '@apps/accounts/services/groups';

export default function ObjectUpdatePage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const formContext = useForm<UserSchemaType>({
    resolver: zodResolver(UserSchema),
    defaultValues: { is_active: true },
  });
  const { user } = useAuth();

  return (
    <UpdatePage
      schema={UserSchema}
      api={API}
      title={t('User')}
      modelProps={modelProps}
      formcontext={formContext}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <TextFieldElement
          name="email"
          label={t('Email')}
          fullWidth
          required
          type="email"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextFieldElement
          name="first_name"
          label={t('First Name')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextFieldElement
          name="last_name"
          label={t('Last Name')}
          required
          fullWidth
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <AsyncAutocompleteElement
          name="preferred_language"
          label={t('Preferred Language')}
          api={CountryAPI.languages}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <AsyncAutocompleteElement
          name="unit"
          label={t('Unit')}
          api={UnitAPI.select}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <AsyncAutocompleteElement
          name="groups"
          label={t('Groups')}
          api={GroupsAPI.select}
          multiple
        />
      </Grid>
      {user?.is_superuser ? (
        <Grid size={{ xs: 12, md: 4 }}>
          <SwitchElement label={t('Is Superuser?')} name="is_superuser" />
        </Grid>
      ) : (
        <></>
      )}
      <Grid size={{ xs: 12, md: 4 }}>
        <SwitchElement label={t('Is Active?')} name="is_active" />
      </Grid>
      <Grid size={{ xs: 12, md: 12 }} mt={2}>
        <Typography mb={1}>{t('Functional Area Leader')}</Typography>

        <AsyncAutocompleteElement
          name="leader_functional_areas"
          label={t('Functional Areas')}
          api={() =>
            FunctionalAreaAPI.select({
              is_active: 1,
            })
          }
          multiple
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12 }} mt={2}>
        <Typography mb={1}>{t('NNC')}</Typography>

        <AsyncAutocompleteElement
          name="managed_units"
          label={t('Managed Units')}
          api={UnitAPI.select}
          multiple
        />
      </Grid>
    </UpdatePage>
  );
}
